import { useMemo } from 'react'
import {
  useHuddleDateFetch,
  type HuddleDateFetcher,
} from './useHuddleDateFetch'
import {
  getHuddleProviderPerformance,
  type HuddleProviderPerformanceResult,
  getHuddleProviderPerformanceOverview,
} from '@services/api/dashboard/huddle.api'
import {
  mapHuddleProviderPerformanceRowsToTable,
  mapHuddleProviderPerformanceSummaryToGridMetrics,
  mapHuddleProviderPerformanceSummaryToMetrics,
  type HuddleProviderPerformanceTableRow,
} from '@utils/dashboardHuddle.util'
import type { DashboardMetricItemData } from '@utils/dashboardMetrics.util'

export type UseHuddleProviderPerformanceOptions = {
  date: string
  enabled?: boolean
  queued?: boolean
}

export type UseHuddleProviderPerformanceResult = {
  data: HuddleProviderPerformanceResult | null
  loading: boolean
  error: string | null
  summaryMetrics: DashboardMetricItemData[]
  gridMetrics: DashboardMetricItemData[]
  providerRows: HuddleProviderPerformanceTableRow[]
}

function useHuddleProviderPerformanceFromFetcher({
  date,
  enabled = true,
  queued = false,
  fetcher,
}: UseHuddleProviderPerformanceOptions & {
  fetcher: HuddleDateFetcher<HuddleProviderPerformanceResult>
}): UseHuddleProviderPerformanceResult {
  const { data, loading, error } = useHuddleDateFetch(date, fetcher, {
    enabled,
    queued,
    errorMessage: 'Failed to load provider performance',
  })

  const summaryMetrics = useMemo(
    () => mapHuddleProviderPerformanceSummaryToMetrics(data?.summary),
    [data?.summary],
  )

  const gridMetrics = useMemo(
    () => mapHuddleProviderPerformanceSummaryToGridMetrics(data?.summary),
    [data?.summary],
  )

  const providerRows = useMemo(
    () => mapHuddleProviderPerformanceRowsToTable(data?.providers),
    [data?.providers],
  )

  return { data, loading, error, summaryMetrics, gridMetrics, providerRows }
}

export function useHuddleProviderPerformance(
  options: UseHuddleProviderPerformanceOptions,
): UseHuddleProviderPerformanceResult {
  return useHuddleProviderPerformanceFromFetcher({
    ...options,
    fetcher: getHuddleProviderPerformance,
  })
}
export function useHuddleProviderPerformanceOverview(
  options: UseHuddleProviderPerformanceOptions,
): UseHuddleProviderPerformanceResult {
  return useHuddleProviderPerformanceFromFetcher({
    ...options,
    fetcher: getHuddleProviderPerformanceOverview,
  })
}
